/**
 * Project Brain API client for the Auteur Cloud web app.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05A. This is the browser half
 * of the hosted Project Brain built by C02C (`auteur_cloud/brain/**`:
 * `memory.py`, `canon.py`, `focus_pack.py`, `operations.py`, `lifecycle.py`,
 * migration `004_brain.sql`). Its job is to RELAY what those stores already
 * computed server-side, never to re-derive it:
 *
 * NO CLIENT-SIDE BRAIN AUTHORITY
 *   Every function here is a thin `fetch` around one server endpoint. None
 *   of them computes a disposition, a Canon approval, an authority decision,
 *   or a freshness verdict — `HostedMemoryStore`, `CanonIndex`,
 *   `HostedFocusPackStore`, `decide_operation_authority()`, and
 *   `index_freshness()` already did that in `auteur_cloud/brain/`. This
 *   client cannot grant what only the server can grant.
 *
 * NO SOURCE TEXT FOR EXCLUDED OR LATENT RECORDS, ENFORCED BY CONTRACT
 *   C02C's privacy guarantee is structural: the disposition table has none
 *   of the content-bearing `DISPOSITION_FIELDS` columns, and the writer
 *   refuses one at any depth. The mirror below therefore has NO content
 *   field on `FocusDispositionRecord` at all — the UI cannot render source
 *   text the server is incapable of returning. `RENDERABLE_DISPOSITION`
 *   names the only disposition whose metadata rows the UI may expand.
 *
 * DELETION AND EXPORT ARE NEVER PLAN-GATED
 *   `lifecycle.py` classifies deletion and export in the constitutional
 *   floor `NEVER_GATE` (capability `CAP-DELETION`). `requestBrainExport`
 *   and `requestBrainDeletion` take NO plan, tier, or entitlement argument,
 *   and no page may present them as upgradable — the constitutional floor
 *   is unconditional.
 *
 * SESSION, NOT TOKEN
 *   Same posture as `api/auth.ts` and `api/billing.ts`: the
 *   `__Host-auteur_session` cookie is the only credential, it is `HttpOnly`,
 *   and every call sets `credentials: 'include'` so the browser attaches it.
 *   Nothing here reads or writes `localStorage`, `sessionStorage`,
 *   `document.cookie`, or an `Authorization` header.
 *
 * THE ROUTES ARE A DOCUMENTED CONTRACT, NOT YET A MOUNTED SERVER
 *   C02C's `brain_status()` reports `deployed: False, mounted: False` at
 *   this base ref — no route in `auteur_cloud/api/routes/` serves the brain
 *   stores yet (seam S-2 in the C02C handoff; mounting is C01C/R8's). Every
 *   function below is written against the exact field names the owning
 *   Python modules already expose, and every page renders an honest
 *   `Unavailable` state when the fetch 404s — which is exactly what happens
 *   today. Nothing here fabricates a brain.
 *
 * BUILD STATUS — compiled and exercised as part of the Vite customer SPA.
 */

import { ApiClientError, requestJson } from './http';

const BASE = '/v1/brain';

/** Thrown for any non-2xx response; pages map `status === 404` to `Unavailable`. */
export class BrainApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'BrainApiError';
  }
}

async function _request<T>(path: string, init: RequestInit): Promise<T> {
  // The session cookie is the ONLY credential. It is HttpOnly and attached
  // by the browser; this client never touches storage or headers for auth.
  try {
    return (await requestJson(path, init)) as T;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new BrainApiError(error.status, error.message);
    }
    throw error;
  }
}

function _get<T>(path: string): Promise<T> {
  return _request<T>(path, { method: 'GET' });
}

function _post<T>(path: string, body: Record<string, unknown>): Promise<T> {
  return _request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/* =====================================================================
 * Response shapes — mirrors of server-side structures, field-for-field.
 * Every name below appears in `auteur_cloud/brain/*.py` at this base ref
 * (checked by `tests/auteur_cloud/test_project_brain_ui.py`).
 * ===================================================================== */

/**
 * Mirror of `auteur_cloud.brain.brain_status()` — the composition root's
 * own truth about whether the brain exists, is deployed, or is mounted.
 * The UI surfaces this verbatim (Visible Orchestration: the page shows the
 * wiring state instead of pretending to be live).
 */
export interface BrainStatus {
  tables: number;
  deployed: boolean;
  mounted: boolean;
  migration_applied: boolean;
  notes: string;
}

/**
 * A hosted memory record (`cloud_memory_records` row via
 * `HostedMemoryStore`). `source_ref` is a reference, never a path and
 * never the text itself (rule R-1): provenance points at where the source
 * lives, it does not carry it.
 */
export interface MemoryRecordView {
  record_id: string;
  project_id: string;
  lane: string;
  kind: string;
  title: string;
  claim: string;
  recall_index_state: 'unindexed' | 'indexed' | 'stale' | 'failed' | null;
  /** Provenance: reference fields mirror `cloud_memory_records` columns. */
  source_type: string;
  source_id: string;
  source_ref: string;
  status: string;
  confidence: number;
  valid_from: string | null;
  /** `stale_after` bounds freshness; the verdict itself is server-computed. */
  stale_after: string | null;
  created_at: string;
  updated_at: string;
  /** Freshness verdict computed server-side; never derived in this file. */
  freshness: 'current' | 'stale' | 'unknown';
}

/**
 * Append-only lifecycle trail row (`LifecycleEvent`). Reason codes are a
 * closed set server-side; the trail carries identifiers and counts only —
 * no content — so the UI renders it as-is.
 */
export interface LifecycleEventView {
  event_id: string;
  record_id: string;
  event_kind: string;
  reason_code: string;
  actor_kind: 'human' | 'service';
  created_at: string;
}

/**
 * A Canon index entry (`cloud_canon_entries`). DT-3: this shape has NO body
 * field and never may — Canon approval is an index over the durable YAML
 * files, not a copy of them. `observed_sha256` is the digest the human
 * reviewer actually saw; approval is refused if it has drifted.
 */
export interface CanonEntryView {
  entry_id: string;
  project_id: string;
  path_ref: string;
  indexed_sha256: string;
  observed_sha256: string | null;
  approval_state: 'pending' | 'approved' | 'superseded';
  approved_by_principal_kind: 'human' | 'service' | null;
  approved_at: string | null;
  /** `CanonIndex.index_freshness()` verdict for this file. */
  freshness: 'current' | 'stale' | 'unknown';
}

/**
 * One disposition row of a Focus Pack. PRIVACY: this interface deliberately
 * has NO content-bearing field — it mirrors a table that has none of the
 * `DISPOSITION_FIELDS` columns. There is nothing here an excluded or latent
 * record could leak.
 */
export interface FocusDispositionRecord {
  record_id: string;
  disposition: 'activated' | 'latent' | 'excluded';
  reason_code: string;
  /** Server-provided human copy via `reason_code_copy()`; never re-derived here. */
  reason_copy: string;
  kind: string;
  decided_at: string;
}

/**
 * A compiled Focus Pack (`StoredPack`). The three lists are mutually
 * exclusive and exhaustive; determinism is a server property, not a UI one.
 */
export interface FocusPackView {
  pack_id: string;
  project_id: string;
  compiled_at: string;
  activated: FocusDispositionRecord[];
  latent: FocusDispositionRecord[];
  excluded: FocusDispositionRecord[];
}

/** `decide_operation_authority()` projection for display. */
export interface AuthorityDecisionView {
  operation: string;
  allowed: boolean;
  reason_code: string;
  decided_by_kind: 'human' | 'service';
  decided_at: string;
}

/** Export/deletion preflight as returned by `lifecycle.py`. */
export interface LifecycleRequestView {
  request_id: string;
  kind: 'export' | 'deletion';
  state: 'pending' | 'prepared' | 'completed' | 'denied';
  /** Never-gated: this shape has no plan, tier, or entitlement field. */
  reason_code: string;
  created_at: string;
}

/**
 * Advisory adjudication state returned by System One for a candidate memory.
 * State is one of:
 * - 'reviewed_by_your_provider' (with provider and model labels)
 * - 'awaiting_review' (no connected provider, or every evaluator unavailable)
 * - 'conflict' (contradicts existing records; person chooses, nothing auto-picked)
 */
export interface CandidateAdjudicationView {
  state: 'reviewed_by_your_provider' | 'awaiting_review' | 'conflict' | string;
  provider: string;
  model: string;
  profile: string;
  reason: string;
  promoted: boolean;
  conflict_set_ids: string[];
  conflicts_with: string[];
  claim_kind: string | null;
}

/**
 * A memory candidate awaiting human review (`cloud_memory_records` with brain_candidate metadata).
 * A candidate NEVER becomes trusted memory before the server confirms adoption.
 */
export interface MemoryCandidateView {
  candidate_id: string;
  project_id: string;
  lane: string;
  kind: string;
  text: string;
  claim?: string;
  origin: string;
  source_ref: string;
  state: 'pending_review' | 'approved' | 'rejected' | string;
  created_at: string;
  adjudication: CandidateAdjudicationView | null;
  explain: string;
  governed_id?: string;
}

export interface CandidateReviewResultView extends MemoryCandidateView {
  governed_id?: string;
}

export type RetrievalMode = 'vector' | 'keyword_fallback' | 'mixed';

export interface SearchHitView {
  chunk_id: string;
  lane: string;
  source_ref: string;
  content: string;
  score: number;
  retrieval_mode: 'vector' | 'keyword_fallback';
  graph_neighbourhood?: {
    ref: string;
    edges: Array<Record<string, unknown>>;
  };
}

export interface SearchBrainResponse {
  project_id: string;
  query: string;
  retrieval_mode: RetrievalMode;
  hits: SearchHitView[];
  explain?: string;
}

export interface CreateMemoryCandidatePayload {
  text: string;
  lane: string;
  kind?: string;
  title?: string;
  sourceRef?: string;
  source_ref?: string;
}

/* =====================================================================
 * Endpoints. One function, one server call, no client-side authority.
 * ===================================================================== */

export function fetchBrainStatus(): Promise<BrainStatus> {
  return _get<BrainStatus>(`${BASE}/status`);
}

export function fetchMemoryRecords(projectId: string): Promise<MemoryRecordView[]> {
  return _get<MemoryRecordView[]>(
    `${BASE}/memory/records?project_id=${encodeURIComponent(projectId)}`,
  );
}

/** Provenance and freshness detail for one record — inspectable by design. */
export function fetchRecordProvenance(recordId: string): Promise<MemoryRecordView> {
  return _get<MemoryRecordView>(`${BASE}/memory/records/${encodeURIComponent(recordId)}`);
}

export function fetchLifecycleTrail(recordId: string): Promise<LifecycleEventView[]> {
  return _get<LifecycleEventView[]>(
    `${BASE}/memory/records/${encodeURIComponent(recordId)}/trail`,
  );
}

/**
 * A correction is a human-authored claim that enters the governed plane as
 * `pending` — it NEVER becomes Canon or trusted memory without the approval
 * gate. The UI submits; only the server decides.
 */
export function submitCorrection(
  projectId: string,
  recordId: string,
  note: string,
): Promise<LifecycleEventView> {
  return _post<LifecycleEventView>(`${BASE}/corrections`, {
    project_id: projectId,
    record_id: recordId,
    note,
  });
}

export function fetchCanonIndex(projectId: string): Promise<CanonEntryView[]> {
  return _get<CanonEntryView[]>(
    `${BASE}/canon?project_id=${encodeURIComponent(projectId)}`,
  );
}

/**
 * Request Canon approval of a reviewed digest. Server-side, approval is
 * human-only by three independent layers (database CHECK, single gated
 * writer, capability model); this call can only ASK, and a service principal
 * asking is refused for the right reason.
 */
export function requestCanonApproval(
  entryId: string,
  observedSha256: string,
): Promise<CanonEntryView> {
  return _post<CanonEntryView>(`${BASE}/canon/approve`, {
    entry_id: entryId,
    observed_sha256: observedSha256,
  });
}

export function fetchFocusPack(projectId: string): Promise<FocusPackView> {
  return _get<FocusPackView>(
    `${BASE}/focus-pack?project_id=${encodeURIComponent(projectId)}`,
  );
}

export function fetchOperationsAuthority(projectId: string): Promise<AuthorityDecisionView[]> {
  return _get<AuthorityDecisionView[]>(
    `${BASE}/operations/authority?project_id=${encodeURIComponent(projectId)}`,
  );
}

/**
 * Export is in the constitutional floor `NEVER_GATE`. Signature takes no
 * plan argument on purpose; adding one is a contract violation the UI test
 * suite fails.
 */
export function requestBrainExport(projectId: string): Promise<LifecycleRequestView> {
  return _post<LifecycleRequestView>(`${BASE}/export`, { project_id: projectId });
}

/**
 * Deletion is capability `CAP-DELETION` in the frozen `NEVER_GATE` floor.
 * No plan, tier, or entitlement argument — ever. The UI must never present
 * deletion as an upgradable feature.
 */
export function requestBrainDeletion(projectId: string): Promise<LifecycleRequestView> {
  return _post<LifecycleRequestView>(`${BASE}/deletion`, { project_id: projectId });
}

/**
 * List pending-review memory candidates for a project.
 * Thin fetch wrapper over GET /v1/brain/memory/candidates?project_id=...&state=pending_review.
 * No plan/tier argument: access is governed strictly by verified session and project membership.
 */
export function fetchPendingMemoryCandidates(projectId: string): Promise<MemoryCandidateView[]> {
  return _get<MemoryCandidateView[]>(
    `${BASE}/memory/candidates?project_id=${encodeURIComponent(projectId)}&state=pending_review`,
  );
}

/**
 * Human review (approve or reject) of a memory candidate with an optional note.
 * Thin fetch wrapper over POST /v1/brain/memory/candidates/{candidate_id}/review.
 * The UI never decides lifecycle state itself and never shows a candidate as trusted
 * memory before the server confirms it. Only a human can approve or reject.
 */
export function reviewMemoryCandidate(
  candidateId: string,
  decision: 'approve' | 'reject',
  note?: string,
  projectId?: string,
): Promise<CandidateReviewResultView> {
  const body: Record<string, unknown> = { decision };
  if (note !== undefined && note.trim().length > 0) {
    body.note = note.trim();
  }
  if (projectId) {
    body.project_id = projectId;
  }
  return _post<CandidateReviewResultView>(
    `${BASE}/memory/candidates/${encodeURIComponent(candidateId)}/review`,
    body,
  );
}

/**
 * Create a memory candidate for human review.
 * Thin fetch wrapper over POST /v1/brain/memory/candidates.
 * Creates a candidate only — never becomes trusted memory before a human approves it.
 * No plan/tier argument.
 */
export function createMemoryCandidate(
  projectId: string,
  payload: CreateMemoryCandidatePayload,
): Promise<MemoryCandidateView> {
  const body: Record<string, unknown> = {
    project_id: projectId,
    text: payload.text,
    lane: payload.lane,
  };
  if (payload.kind !== undefined) {
    body.kind = payload.kind;
  }
  if (payload.title !== undefined) {
    body.title = payload.title;
  }
  const sourceRef = payload.sourceRef ?? payload.source_ref;
  if (sourceRef !== undefined) {
    body.source_ref = sourceRef;
  }
  return _post<MemoryCandidateView>(`${BASE}/memory/candidates`, body);
}

/**
 * Search the project's memory and semantic plane.
 * Thin fetch wrapper over GET /v1/brain/search?project_id=...&q=...
 * Merges vector and keyword hits, returning retrieval_mode ('vector', 'keyword_fallback', or 'mixed').
 * No plan/tier argument.
 */
export function searchBrain(
  projectId: string,
  query: string,
): Promise<SearchBrainResponse> {
  return _get<SearchBrainResponse>(
    `${BASE}/search?project_id=${encodeURIComponent(projectId)}&q=${encodeURIComponent(query)}`,
  );
}
